import { useEffect, useState } from "react";
import PortalLayout from "@/components/PortalLayout";
import { api, apiErr } from "@/lib/api";
import { usePortalAuth } from "@/context/PortalAuth";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { ReplyButton } from "@/components/ReplyThread";

const TYPES = ["Statement", "Transfer", "MinorWork", "MajorWork", "Other"];

export default function PortalApplications() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "Statement", unit_id: "", details: "" });
  const { me } = usePortalAuth();
  const units = me?.owned_units || [];

  const load = () => api.get("/portal/applications").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/portal/applications", { ...form, unit_id: form.unit_id || units[0]?.id, attachments: [] });
      toast.success("Application submitted.");
      setForm({ type: "Statement", unit_id: "", details: "" });
      setOpen(false);
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <PortalLayout
      subtitle="Requests"
      title="Applications"
      actions={
        <button onClick={() => setOpen(!open)} data-testid="new-application"
          className="inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-4 py-2 text-sm">
          <Plus size={14}/> New
        </button>
      }
    >
      {open && (
        <form onSubmit={submit} className="mb-6 bg-white border border-stone-200 rounded-2xl p-6 space-y-3" data-testid="application-form">
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Type</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
              data-testid="app-type"
              className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-base outline-none focus:border-terracotta">
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {units.length > 1 && (
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Unit</label>
              <select value={form.unit_id} onChange={(e) => setForm({ ...form, unit_id: e.target.value })}
                className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-base outline-none focus:border-terracotta">
                <option value="">— select unit —</option>
                {units.map((u) => <option key={u.id} value={u.id}>{u.building}{u.wing ? "/" + u.wing : ""}/{u.unit_no}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Details</label>
            <textarea rows="4" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} required
              data-testid="app-details"
              className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-base outline-none focus:border-terracotta"/>
          </div>
          <div className="flex justify-end"><button type="submit" data-testid="app-submit" className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-6 py-2.5 text-sm">Submit</button></div>
        </form>
      )}

      <div className="space-y-3">
        {rows.map((a) => (
          <div key={a.id} className="bg-white border border-stone-200 rounded-2xl p-5" data-testid={`app-${a.id}`}>
            <div className="flex items-center justify-between">
              <div className="editorial text-lg">{a.type}</div>
              <Status s={a.status}/>
            </div>
            <p className="text-sm text-stone-600 mt-2 whitespace-pre-wrap">{a.details}</p>
            <div className="text-xs text-stone-500 mt-2">{a.created_at?.slice(0, 16).replace("T", " ")}</div>
            {a.decision_remarks && <div className="mt-2 text-sm text-stone-700 bg-sand-100 border border-stone-200 rounded-lg p-3">Decision: {a.decision_remarks}</div>}
            <div className="mt-3 flex justify-end">
              <ReplyButton entityType="application" entityId={a.id} entityTitle={`${a.type} Application`} testid={`reply-app-${a.id}`}/>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center text-stone-500">No applications submitted yet.</div>}
      </div>
    </PortalLayout>
  );
}
function Status({ s }) {
  const m = { Submitted: "bg-amber-100 text-amber-800", Approved: "bg-emerald-100 text-emerald-800", Rejected: "bg-red-100 text-red-800", "Under Review": "bg-blue-100 text-blue-800" };
  return <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${m[s] || "bg-stone-100"}`}>{s}</span>;
}
